/**
 * Accessibility Utility Functions for YearlyCalendar Component
 * Generates ARIA labels and handles accessibility concerns
 */
import { format } from 'date-fns';
import { CalendarEvent, DayObject } from '../../../contracts/YearlyCalendarContracts';

/**
 * Generate ARIA label for a calendar day
 */
export const generateDayAriaLabel = (
  day: DayObject,
  events: CalendarEvent[],
  customAriaLabel?: string
): string => {
  const parts: string[] = [];
  
  // Full date (e.g., "Wednesday, December 25, 2024")
  parts.push(format(day.date, 'EEEE, MMMM d, yyyy'));
  
  // Current day indicator
  if (day.current) {
    parts.push('Current Day');
  }
  
  // Selected indicator
  if (day.selected) {
    parts.push('Selected');
  }
  
  // Event count and instructions
  if (events.length > 0) {
    const eventText = events.length === 1 ? '1 event' : `${events.length} events`;
    parts.push(`${eventText}. Press enter to view or add events.`);
  } else {
    parts.push('has no events. Press enter to add events.');
  }
  
  // Custom ARIA label from callback
  if (customAriaLabel) {
    parts.push(customAriaLabel);
  }
  
  // Navigation hint
  parts.push('Use arrow keys to navigate through the calendar items');
  
  return parts.filter(Boolean).join(' ');
};

/**
 * Generate ARIA label for an event
 */
export const generateEventAriaLabel = (event: CalendarEvent): string => {
  const parts: string[] = [];
  
  // Event name
  parts.push(event.name);
  
  // Subject if available
  if (event.subject) {
    parts.push(event.subject);
  }
  
  // Time metadata if available
  if (event.timeMetaData) {
    parts.push(event.timeMetaData);
  }
  
  // Event type
  if (event.eventTypeName) {
    parts.push(event.eventTypeName);
  }
  
  // Holiday indicator
  if (event.isPublicHoliday) {
    parts.push('Holiday');
    if (event.holidayType) {
      parts.push(event.holidayType);
    }
  }
  
  // Custom ARIA label
  if (event.ariaLabel) {
    parts.push(event.ariaLabel);
  }
  
  return parts.filter(Boolean).join(', ');
};

/**
 * Generate ARIA label for calendar navigation button
 */
export const generateNavButtonAriaLabel = (
  direction: 'previous' | 'next',
  mode: 'day' | 'month' | 'year',
  targetDate: Date
): string => {
  const action = direction === 'previous' ? 'Move to previous' : 'Move to next';
  
  switch (mode) {
    case 'day':
      return `${action} month ${format(targetDate, 'MMMM yyyy')}`;
    case 'month':
      return `${action} year ${format(targetDate, 'yyyy')}`;
    case 'year':
      return `${action} year range`;
    default:
      return `${action} ${mode}`;
  }
};

/**
 * Generate ARIA label for mode toggle button
 */
export const generateModeToggleAriaLabel = (currentMode: string, targetDate: Date): string => {
  switch (currentMode) {
    case 'day':
      return `Currently viewing ${format(targetDate, 'MMMM yyyy')}. Click to switch to year view.`;
    case 'month':
      return `Currently viewing ${format(targetDate, 'yyyy')}. Click to switch to year picker.`;
    case 'year':
      return `Year picker. Select a year.`;
    default:
      return `View mode: ${currentMode}`;
  }
};

/**
 * Generate ARIA label for the calendar table
 */
export const generateCalendarAriaLabel = (
  mode: string,
  targetDate: Date,
  calendarName?: string
): string => {
  const baseName = calendarName || 'Calendar';
  
  switch (mode) {
    case 'day':
      return `${baseName} - ${format(targetDate, 'MMMM yyyy')} Monthly View`;
    case 'month':
      return `${baseName} - ${format(targetDate, 'yyyy')} Year Overview`;
    case 'year':
      return `${baseName} - Year Picker`;
    case 'richcc-year':
      return `${baseName} - Full Year View ${format(targetDate, 'yyyy')}`;
    default:
      return baseName;
  }
};

/**
 * Generate ARIA label for popup
 */
export const generatePopupAriaLabel = (date: Date, eventCount: number): string => {
  const dateStr = format(date, 'EEEE, MMMM d, yyyy');
  const eventText = eventCount === 0
    ? 'No events'
    : eventCount === 1
    ? '1 event'
    : `${eventCount} events`;
  
  return `Events for ${dateStr}. ${eventText}. Press Escape to close.`;
};

/**
 * Generate ARIA label for "more events" indicator
 */
export const generateMoreEventsAriaLabel = (hiddenCount: number): string => {
  return `${hiddenCount} more ${hiddenCount === 1 ? 'event' : 'events'}. Press enter to view all.`;
};

/**
 * Screen reader live region announcement helper
 */
export const announceToScreenReader = (message: string, politeness: 'polite' | 'assertive' = 'polite'): void => {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', politeness);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.style.cssText = `
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `;
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

/**
 * Focus trap helper for popups
 */
export const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
  const focusableSelectors = [
    'button:not([disabled])',
    'a[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');
  
  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors));
};

/**
 * Trap focus within a container (for modal/popup)
 */
export const createFocusTrap = (container: HTMLElement): (() => void) => {
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    
    const focusables = getFocusableElements(container);
    if (focusables.length === 0) return;
    
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  
  container.addEventListener('keydown', handleKeyDown);
  
  // Return cleanup function
  return () => {
    container.removeEventListener('keydown', handleKeyDown);
  };
};
